/**
 * v1.14.71.0 — Admin AI Site Doctor Dashboard (FASE 7B).
 *
 * Centralizza la visualizzazione degli errori catturati dal collector FASE 7A:
 *  - 4 KPI card (totale, oggi, critical aperti, top recurring)
 *  - Tabella errori filtrabile (severity, source, status, search, date range)
 *  - Modal dettaglio con tutti i campi sanitizzati
 *  - Status change (new → investigating → fixed/ignored)
 *  - Export CSV filtrato
 *
 * Endpoint usati:
 *   GET  /api/admin/ai-site-doctor/errors
 *   GET  /api/admin/ai-site-doctor/errors/stats
 *   GET  /api/admin/ai-site-doctor/errors/{id}
 *   POST /api/admin/ai-site-doctor/errors/{id}/status
 *   GET  /api/admin/ai-site-doctor/errors-export.csv
 *
 * Pattern UI: coerente con PraticheDOPage / CRM admin pages.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Loader2, Search, X, Copy, Download, RefreshCw, AlertTriangle,
  Activity, ShieldCheck, Zap, ChevronRight, Filter,
  Sparkles, Brain, FileCode, CheckCircle2, Wallet,
  HeartPulse, Bell, Mail, Send, Eye, EyeOff,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/ai-site-doctor`;

const SEVERITY_BADGE = {
  critical: "bg-red-100 text-red-800 border-red-300",
  high:     "bg-orange-100 text-orange-800 border-orange-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-300",
  low:      "bg-slate-100 text-slate-700 border-slate-300",
};

const SOURCE_BADGE = {
  frontend: "bg-indigo-100 text-indigo-800",
  backend:  "bg-emerald-100 text-emerald-800",
  api:      "bg-sky-100 text-sky-800",
  database: "bg-purple-100 text-purple-800",
  auth:     "bg-rose-100 text-rose-800",
  pdf:      "bg-yellow-100 text-yellow-800",
  unknown:  "bg-slate-100 text-slate-600",
};

const STATUS_BADGE = {
  new:           "bg-blue-100 text-blue-800",
  investigating: "bg-amber-100 text-amber-800",
  fixed:         "bg-emerald-100 text-emerald-800",
  ignored:       "bg-slate-100 text-slate-600",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function HealthBanner({ stats }) {
  if (!stats) return null;
  const critical = (stats.count_by_severity || {}).critical || 0;
  const high = (stats.count_by_severity || {}).high || 0;
  const today = stats.count_today || 0;

  let status, label, color;
  if (critical > 0) {
    status = "red"; label = `${critical} errori critici aperti`;
    color = "bg-red-50 border-red-300 text-red-900";
  } else if (high > 0 || today > 10) {
    status = "yellow"; label = `${today} errori oggi (${high} di severità alta)`;
    color = "bg-amber-50 border-amber-300 text-amber-900";
  } else {
    status = "green"; label = "Sito sano — nessun errore critico";
    color = "bg-emerald-50 border-emerald-300 text-emerald-900";
  }
  const icon = status === "red"
    ? <AlertTriangle className="w-5 h-5" />
    : status === "yellow"
      ? <Activity className="w-5 h-5" />
      : <ShieldCheck className="w-5 h-5" />;
  return (
    <div data-testid="aisd-health-banner"
         className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${color}`}>
      {icon}
      <span className="font-medium text-sm sm:text-base">{label}</span>
    </div>
  );
}

function KPICard({ label, value, sublabel, icon: Icon, testid, color = "emerald" }) {
  return (
    <div data-testid={testid}
         className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</span>
        <Icon className={`w-4 h-4 text-${color}-600`} />
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}

function FilterBar({ filters, setFilters, onExport, onRefresh, loading }) {
  return (
    <div data-testid="aisd-filter-bar"
         className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[180px]">
        <label className="block text-xs font-medium text-slate-600 mb-1">Cerca</label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="aisd-search-input"
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="error_message..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Severity</label>
        <select data-testid="aisd-filter-severity"
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">Tutte</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Source</label>
        <select data-testid="aisd-filter-source"
                value={filters.source}
                onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">Tutti</option>
          <option value="frontend">Frontend</option>
          <option value="backend">Backend</option>
          <option value="api">API</option>
          <option value="database">Database</option>
          <option value="auth">Auth</option>
          <option value="pdf">PDF</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
        <select data-testid="aisd-filter-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="">Tutti</option>
          <option value="new">New</option>
          <option value="investigating">Investigating</option>
          <option value="fixed">Fixed</option>
          <option value="ignored">Ignored</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Periodo</label>
        <select data-testid="aisd-filter-range"
                value={filters.range}
                onChange={(e) => setFilters({ ...filters, range: e.target.value })}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
          <option value="24h">Ultime 24h</option>
          <option value="7d">Ultimi 7g</option>
          <option value="30d">Ultimi 30g</option>
          <option value="all">Tutti</option>
        </select>
      </div>
      <div className="flex gap-2 ml-auto">
        <button data-testid="aisd-refresh-btn"
                onClick={onRefresh}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
        <button data-testid="aisd-export-csv-btn"
                onClick={onExport}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm hover:bg-emerald-800">
          <Download className="w-4 h-4" />
          Esporta CSV
        </button>
      </div>
    </div>
  );
}

function ErrorRow({ row, onClick }) {
  const aiStatus = row.ai_analysis
    ? (row.ai_analysis_cloned_from ? "cloned" : "analyzed")
    : "none";
  return (
    <tr data-testid={`aisd-row-${row.id}`}
        onClick={() => onClick(row.id)}
        className="border-b border-slate-100 hover:bg-emerald-50/30 cursor-pointer transition-colors">
      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDate(row.created_at)}</td>
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_BADGE[row.source] || SOURCE_BADGE.unknown}`}>
          {row.source}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_BADGE[row.severity] || SEVERITY_BADGE.medium}`}>
          {row.severity}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[row.status] || STATUS_BADGE.new}`}>
          {row.status}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-800 max-w-md">
        <div className="truncate" title={row.error_message}>{row.error_message || "—"}</div>
        {row.component_name && (
          <div className="text-xs text-slate-500 truncate">{row.component_name}</div>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        {row.occurrences_count > 1 ? (
          <span className="inline-block px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-xs font-bold">
            ×{row.occurrences_count}
          </span>
        ) : <span className="text-slate-400 text-xs">1</span>}
      </td>
      <td className="px-4 py-3 text-center">
        {aiStatus === "analyzed" && (
          <span title="Analizzato AI" className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
        )}
        {aiStatus === "cloned" && (
          <span title="Cloned analysis" className="inline-block w-2.5 h-2.5 rounded-full bg-sky-500" />
        )}
        {aiStatus === "none" && (
          <span title="Non analizzato" className="inline-block w-2.5 h-2.5 rounded-full bg-slate-300" />
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
      </td>
    </tr>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="mb-4">
      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 border-b border-slate-200 pb-1">{title}</h4>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 py-1.5">
      <span className="text-xs text-slate-500 sm:w-36 flex-shrink-0">{label}</span>
      <span className={`text-sm text-slate-800 break-words ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

const GRAVITA_BADGE = {
  critical: "bg-red-600 text-white",
  high:     "bg-orange-500 text-white",
  medium:   "bg-amber-400 text-slate-900",
  low:      "bg-slate-300 text-slate-900",
};

function AIAnalysisPanel({ detail, onAnalyzed }) {
  const [busy, setBusy] = useState(false);
  const [localAnalysis, setLocalAnalysis] = useState(detail.ai_analysis || null);
  const [modelUsed, setModelUsed] = useState(detail.ai_model_used || null);
  const [costEur, setCostEur] = useState(detail.ai_cost_eur || 0);
  const [clonedFrom, setClonedFrom] = useState(detail.ai_analysis_cloned_from || null);

  const analyze = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/analyze/${detail.id}${force ? "?force=true" : ""}`,
        {}, { withCredentials: true });
      setLocalAnalysis(data.ai_analysis);
      setModelUsed(data.ai_model_used);
      setCostEur(data.ai_cost_eur || 0);
      setClonedFrom(data.cloned_from || null);
      toast.success(data.cached ? "Analisi cached caricata" : "Analisi AI completata");
      onAnalyzed && onAnalyzed();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Budget mensile esaurito. Aumenta il cap nel tab Budget.");
      } else if (e.response?.status === 502) {
        toast.error("LLM non risponde. Riprova fra qualche istante.");
      } else {
        toast.error("Analisi AI fallita: " + (e.response?.data?.detail || e.message));
      }
    } finally { setBusy(false); }
  };

  if (!localAnalysis) {
    return (
      <div data-testid="aisd-ai-analysis-empty"
           className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Brain className="w-5 h-5 text-purple-700" />
          <span className="text-sm font-medium text-purple-900">Analisi AI non ancora eseguita</span>
        </div>
        <p className="text-xs text-purple-700 mb-3">
          Lancia l&apos;analizzatore AI (Claude Haiku) per ottenere root cause, soluzione consigliata e patch suggerita.
        </p>
        <button data-testid="aisd-analyze-ai-btn"
                disabled={busy}
                onClick={() => analyze(false)}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-purple-700 text-white rounded-lg text-sm font-medium hover:bg-purple-800 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Analizzando…" : "Analizza con AI"}
        </button>
      </div>
    );
  }

  const copyPatch = () => {
    navigator.clipboard.writeText(localAnalysis.patch_suggerita || "").then(
      () => toast.success("Patch copiata"),
      () => toast.error("Copia fallita"));
  };

  const confidence = localAnalysis.confidence_score || 0;
  const confColor = confidence >= 0.8 ? "emerald" : confidence >= 0.5 ? "amber" : "rose";

  return (
    <div data-testid="aisd-ai-analysis-result"
         className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 space-y-3">
      {/* Header con titolo + gravità + confidence */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-base font-bold text-purple-900 flex-1 min-w-0">
          {localAnalysis.titolo_problema}
        </h3>
        <div className="flex items-center gap-2">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${GRAVITA_BADGE[localAnalysis.gravita] || GRAVITA_BADGE.medium}`}>
            {localAnalysis.gravita?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Confidence bar */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-600">Confidence</span>
          <span className={`font-mono font-bold text-${confColor}-700`}>
            {(confidence * 100).toFixed(0)}%
          </span>
        </div>
        <div className="w-full bg-white rounded-full h-2 overflow-hidden">
          <div className={`h-2 bg-${confColor}-500 transition-all`}
                style={{ width: `${confidence * 100}%` }} />
        </div>
      </div>

      {/* Metadati: modello + costo + cloned */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 bg-white/60 rounded p-2">
        <span>📦 Modello: <code className="font-mono text-purple-900">{modelUsed}</code></span>
        <span>💰 Costo: <code className="font-mono">{(costEur || 0).toFixed(5)}€</code></span>
        {clonedFrom && (
          <span className="text-emerald-700">🔁 Cloned from <code className="font-mono text-xs">{clonedFrom.slice(0, 8)}…</code></span>
        )}
      </div>

      {/* Descrizione semplice */}
      <div className="bg-white rounded-lg p-3 border border-purple-100">
        <p className="text-sm text-slate-800 leading-relaxed">{localAnalysis.descrizione_semplice}</p>
      </div>

      {/* Collapsible: descrizione tecnica + causa + impatto */}
      <details className="bg-white/60 rounded-lg border border-purple-100">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-purple-900 hover:bg-white">
          Dettagli tecnici
        </summary>
        <div className="px-3 pb-3 space-y-2 text-sm text-slate-700">
          {localAnalysis.descrizione_tecnica && (
            <div><span className="font-medium">Tecnico:</span> {localAnalysis.descrizione_tecnica}</div>
          )}
          {localAnalysis.dove_si_verifica && (
            <div><span className="font-medium">Dove:</span> <code className="text-xs">{localAnalysis.dove_si_verifica}</code></div>
          )}
          {localAnalysis.probabile_causa && (
            <div><span className="font-medium">Root cause:</span> {localAnalysis.probabile_causa}</div>
          )}
          {localAnalysis.impatto_utente && (
            <div><span className="font-medium">Impatto utente:</span> {localAnalysis.impatto_utente}</div>
          )}
        </div>
      </details>

      {/* Soluzione consigliata */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <span className="text-sm font-medium text-emerald-900">Soluzione consigliata</span>
        </div>
        <p className="text-sm text-emerald-800 whitespace-pre-wrap">{localAnalysis.soluzione_consigliata}</p>
      </div>

      {/* Patch suggerita */}
      {localAnalysis.patch_suggerita && (
        <div className="bg-slate-900 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800">
            <div className="flex items-center gap-1.5">
              <FileCode className="w-4 h-4 text-emerald-300" />
              <span className="text-xs font-medium text-emerald-300">Patch suggerita</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ml-1 bg-${localAnalysis.rischio_correzione === 'low' ? 'emerald' : localAnalysis.rischio_correzione === 'medium' ? 'amber' : 'rose'}-700 text-white`}>
                Rischio: {localAnalysis.rischio_correzione}
              </span>
            </div>
            <button data-testid="aisd-copy-patch-btn"
                    onClick={copyPatch}
                    className="text-xs px-2 py-0.5 bg-slate-700 text-slate-200 hover:bg-slate-600 rounded">
              <Copy className="w-3 h-3 inline" /> Copia
            </button>
          </div>
          <pre className="text-xs text-emerald-300 p-3 overflow-x-auto whitespace-pre-wrap break-all max-h-64 font-mono">
            {localAnalysis.patch_suggerita}
          </pre>
        </div>
      )}

      {/* File coinvolti + Test */}
      {(localAnalysis.file_coinvolti?.length > 0 || localAnalysis.test_da_eseguire?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {localAnalysis.file_coinvolti?.length > 0 && (
            <div className="bg-white/70 rounded-lg p-2">
              <div className="text-xs font-medium text-slate-600 mb-1">File coinvolti</div>
              <ul className="text-xs space-y-0.5">
                {localAnalysis.file_coinvolti.map((f, i) => (
                  <li key={i}><code className="text-xs text-slate-800">{f}</code></li>
                ))}
              </ul>
            </div>
          )}
          {localAnalysis.test_da_eseguire?.length > 0 && (
            <div className="bg-white/70 rounded-lg p-2">
              <div className="text-xs font-medium text-slate-600 mb-1">Test da eseguire</div>
              <ul className="text-xs space-y-0.5">
                {localAnalysis.test_da_eseguire.map((t, i) => (
                  <li key={i}><code className="text-xs text-slate-800">{t}</code></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Re-analyze */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-purple-200">
        <span className="text-xs text-slate-500 italic">
          Modalità: <strong>Diagnose Only</strong> (auto-fix in FASE 7D)
        </span>
        <button data-testid="aisd-reanalyze-ai-btn"
                disabled={busy}
                onClick={() => analyze(true)}
                className="text-xs text-purple-700 hover:text-purple-900 disabled:opacity-50">
          {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <RefreshCw className="w-3 h-3 inline" />}
          {" "}Ri-analizza
        </button>
      </div>
    </div>
  );
}

function FileLink({ path, lineNumber, editorTemplate }) {
  // Default VS Code locale; ammette anche github + sourcegraph templates
  const tpl = editorTemplate || "vscode://file/{path}:{line}";
  const url = tpl
    .replace("{path}", path)
    .replace("{line}", lineNumber || 1);
  return (
    <a data-testid={`aisd-open-in-editor-${path.replace(/[^a-z0-9]/gi, "_")}`}
       href={url}
       target="_blank"
       rel="noreferrer"
       title="Apri in editor (VS Code/GitHub)"
       onClick={(e) => {
         // Fallback su copy se vscode:// non gestito
         if (tpl.startsWith("vscode://")) {
           navigator.clipboard?.writeText(`${path}${lineNumber ? ":" + lineNumber : ""}`).catch(() => {});
         }
       }}
       className="inline-block ml-1 px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded transition">
      ↗ apri
    </a>
  );
}

function SafetyChecks({ checks }) {
  if (!checks) return null;
  const items = [
    ["no_secret_files", "No file .env/secrets/keys"],
    ["no_authentication_code", "No file auth/login/jwt"],
    ["no_payment_code", "No file payment/stripe/billing"],
    ["no_db_migration", "No migration/alembic"],
    ["no_critical_models", "No models/user|admin"],
    ["no_critical_diff_patterns", "No DELETE/DROP/eval/exec"],
    ["passes_static_lint", "Lint OK (placeholder FASE 7E)"],
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
      {items.map(([key, label]) => {
        const ok = checks[key];
        return (
          <div key={key} data-testid={`aisd-safety-${key}`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded ${ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            <span>{ok ? "✓" : "✗"}</span>
            <span>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function SuggestFixPanel({ detail, mode }) {
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const editorTemplate = mode?.editor_url_template || "vscode://file/{path}:{line}";

  // Estrai eventuale ":line" da dove_si_verifica per il deep-link
  const inferredLine = (() => {
    const dove = detail.ai_analysis?.dove_si_verifica || "";
    const m = dove.match(/:(\d+)\b/);
    return m ? parseInt(m[1], 10) : null;
  })();

  if (!detail.ai_analysis) {
    return (
      <div data-testid="aisd-suggest-fix-need-analysis"
            className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        Esegui prima l&apos;analisi AI per generare una proposta di fix.
      </div>
    );
  }

  if (mode?.mode === "diagnose_only") {
    return (
      <div data-testid="aisd-suggest-fix-disabled"
            className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-3 text-xs text-slate-600">
        Modalità <strong>Diagnose Only</strong>. Cambia modalità in <em>Impostazioni</em> nel tab Budget per generare proposte di fix.
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/suggest-fix/${detail.id}`, {}, { withCredentials: true });
      setProposal(data);
      toast.success("Fix proposal generata");
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (!proposal) {
    return (
      <button data-testid="aisd-generate-fix-btn"
              disabled={busy}
              onClick={generate}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-700 text-white rounded-lg text-sm font-medium hover:bg-indigo-800 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCode className="w-4 h-4" />}
        Genera Fix Suggerito
      </button>
    );
  }

  const copyDiff = () => {
    navigator.clipboard.writeText(proposal.proposed_patch?.diff_unified || "").then(
      () => toast.success("Diff copiato"),
      () => toast.error("Copia fallita"));
  };

  const downloadDiff = () => {
    const blob = new Blob([proposal.proposed_patch?.diff_unified || ""], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `fix-${proposal.fix_id.slice(0, 8)}.diff`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  };

  const submitApprove = async (reason) => {
    setBusy(true);
    setApplyResult(null);
    try {
      const { data } = await axios.post(
        `${API}/approve-fix/${proposal.fix_id}`,
        { approval_reason: reason }, { withCredentials: true });
      setApplyResult(data);
      if (data.status === "applied") {
        toast.success("Fix applicato con successo");
      } else if (data.status === "apply_failed_rolled_back") {
        toast.warning("Fix applicato ma test falliti → rollback automatico");
      } else {
        toast.error("Apply fallito: " + (data.apply_stderr || data.status));
      }
      setApproveDialog(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || e.message;
      toast.error("Approve fallito: " + msg);
    } finally { setBusy(false); }
  };

  const submitReject = async (reason) => {
    setBusy(true);
    try {
      await axios.post(`${API}/reject-fix/${proposal.fix_id}`,
                       { rejection_reason: reason }, { withCredentials: true });
      toast.success("Fix rifiutato");
      setRejectDialog(false);
      setProposal({ ...proposal, status: "rejected" });
    } catch (e) {
      toast.error("Reject fallito");
    } finally { setBusy(false); }
  };

  const riskColor = proposal.risk_level === "low"
    ? "emerald" : proposal.risk_level === "medium" ? "amber" : "rose";

  return (
    <div data-testid="aisd-fix-proposal" className="space-y-3">
      {/* Header: risk + category */}
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-indigo-200">
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-${riskColor}-100 text-${riskColor}-800`}>
          Rischio: {proposal.risk_level}
        </span>
        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800">
          {proposal.fix_category}
        </span>
        <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">
          Type: {proposal.proposed_patch?.type}
        </span>
        {proposal.requires_approval && (
          <span data-testid="aisd-requires-approval"
                 className="inline-block px-2 py-0.5 rounded-full text-xs bg-amber-200 text-amber-900 font-medium">
            ⚠ Richiede approvazione admin (FASE 7E)
          </span>
        )}
      </div>

      {/* File coinvolti */}
      {proposal.proposed_patch?.files_affected?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">File coinvolti</div>
          <ul className="space-y-0.5">
            {proposal.proposed_patch.files_affected.map((f, i) => (
              <li key={i} className="text-xs flex items-center">
                <code className="bg-slate-100 px-1.5 py-0.5 rounded">{f}</code>
                <FileLink path={f} lineNumber={inferredLine} editorTemplate={editorTemplate} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Diff unified */}
      {proposal.proposed_patch?.diff_unified && (
        <div className="bg-slate-900 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800">
            <span className="text-xs font-medium text-emerald-300">Diff unified</span>
            <div className="flex gap-1.5">
              <button data-testid="aisd-copy-diff-btn"
                      onClick={copyDiff}
                      className="text-xs px-2 py-0.5 bg-slate-700 text-slate-200 hover:bg-slate-600 rounded">
                <Copy className="w-3 h-3 inline" /> Copia
              </button>
              <button data-testid="aisd-download-diff-btn"
                      onClick={downloadDiff}
                      className="text-xs px-2 py-0.5 bg-slate-700 text-slate-200 hover:bg-slate-600 rounded">
                <Download className="w-3 h-3 inline" /> .diff
              </button>
            </div>
          </div>
          <pre className="text-xs text-emerald-300 p-3 overflow-x-auto whitespace-pre-wrap break-all max-h-64 font-mono">
            {proposal.proposed_patch.diff_unified}
          </pre>
        </div>
      )}

      {/* Safety checks */}
      <div>
        <div className="text-xs font-medium text-slate-700 mb-1">Safety checks</div>
        <SafetyChecks checks={proposal.safety_checks} />
        {proposal.safety_checks?.warnings?.length > 0 && (
          <div className="mt-2 bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-800">
            <strong>Warnings:</strong>
            <ul className="list-disc list-inside mt-1">
              {proposal.safety_checks.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Test commands */}
      {proposal.proposed_patch?.test_commands?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Test consigliati</div>
          <ul className="space-y-0.5">
            {proposal.proposed_patch.test_commands.map((t, i) => (
              <li key={i} className="text-xs text-slate-700">• {t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Apply Fix (FASE 7E) */}
      <div className="flex items-center justify-between pt-2 border-t border-indigo-200">
        <span className="text-xs text-slate-500 italic">
          fix_id: <code className="text-xs">{proposal.fix_id.slice(0, 8)}…</code>
          {proposal.status && proposal.status !== "proposed" && (
            <span className="ml-2 px-2 py-0.5 bg-slate-200 rounded text-slate-700">
              status: {proposal.status}
            </span>
          )}
        </span>
        {proposal.status === "proposed" && (
          <div className="flex gap-2">
            <button data-testid="aisd-reject-fix-btn"
                    disabled={busy}
                    onClick={() => setRejectDialog(true)}
                    className="text-xs px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 rounded">
              Rifiuta
            </button>
            <button data-testid="aisd-apply-fix-btn"
                    disabled={busy || !proposal.safety_passes}
                    title={proposal.safety_passes ? "Applica il fix (richiede approvazione)" : "Safety check fallito - apply bloccato"}
                    onClick={() => setApproveDialog(true)}
                    className="text-xs px-3 py-1.5 bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed">
              Applica Ora
            </button>
          </div>
        )}
      </div>

      {/* Apply result panel */}
      {applyResult && <ApplyResultPanel result={applyResult} />}

      {/* Approve dialog */}
      {approveDialog && (
        <ApprovalDialog
          title="Approva e applica fix"
          confirmLabel="Approva e applica"
          minLength={10}
          onConfirm={submitApprove}
          onCancel={() => setApproveDialog(false)}
          busy={busy}
          dataTestid="aisd-approve-dialog"
          extraConfirmCheckbox="Confermo di aver letto il diff e i safety check"
        />
      )}

      {/* Reject dialog */}
      {rejectDialog && (
        <ApprovalDialog
          title="Rifiuta fix"
          confirmLabel="Rifiuta"
          minLength={5}
          onConfirm={submitReject}
          onCancel={() => setRejectDialog(false)}
          busy={busy}
          dataTestid="aisd-reject-dialog"
          textareaLabel="Motivo del rifiuto (≥5 caratteri)"
        />
      )}
    </div>
  );
}

function ApprovalDialog({ title, confirmLabel, minLength, onConfirm, onCancel,
                          busy, dataTestid, extraConfirmCheckbox, textareaLabel }) {
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState(!extraConfirmCheckbox);
  const valid = reason.trim().length >= minLength && checked;
  return (
    <div data-testid={dataTestid}
         className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
         onClick={onCancel}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3">{title}</h3>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {textareaLabel || `Motivo (≥${minLength} caratteri, obbligatorio)`}
        </label>
        <textarea data-testid={`${dataTestid}-reason`}
                   value={reason}
                   onChange={(e) => setReason(e.target.value)}
                   rows={3}
                   className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                   placeholder="Es: Patch verificata manualmente, modifica safe in null check"
                   autoFocus />
        <div className="text-xs text-slate-500 mt-1">{reason.trim().length}/{minLength} caratteri minimo</div>
        {extraConfirmCheckbox && (
          <label className="flex items-start gap-2 mt-3 text-xs text-slate-700 cursor-pointer">
            <input data-testid={`${dataTestid}-confirm`}
                    type="checkbox" checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                    className="mt-0.5" />
            <span>{extraConfirmCheckbox}</span>
          </label>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50">
            Annulla
          </button>
          <button data-testid={`${dataTestid}-submit`}
                  disabled={!valid || busy}
                  onClick={() => onConfirm(reason)}
                  className="px-3 py-1.5 bg-emerald-700 text-white rounded text-sm hover:bg-emerald-800 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApplyResultPanel({ result }) {
  const tests = result.test_results || [];
  const failed = tests.filter((t) => t.exit_code !== 0).length;
  const passed = tests.filter((t) => t.exit_code === 0).length;
  const color = result.status === "applied"
    ? "emerald" : result.status === "apply_failed_rolled_back" ? "amber" : "rose";
  const icon = result.status === "applied"
    ? "✓" : result.status === "apply_failed_rolled_back" ? "⚠" : "✗";
  const label = result.status === "applied"
    ? "Fix applicato con successo"
    : result.status === "apply_failed_rolled_back"
      ? "Test falliti → rollback automatico eseguito"
      : "Apply fallito";

  return (
    <div data-testid="aisd-apply-result"
         className={`mt-3 bg-${color}-50 border border-${color}-200 rounded-lg p-3 space-y-2`}>
      <div className={`flex items-center gap-2 text-${color}-900 font-medium`}>
        <span className="text-xl">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-xs text-slate-700">
        Apply ID: <code className="font-mono">{result.apply_id?.slice(0, 8)}…</code>
        {tests.length > 0 && (
          <span className="ml-3">Test: {passed} passed, {failed} failed</span>
        )}
        {result.rolled_back && (
          <span className="ml-3 px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded text-xs">backup ripristinato</span>
        )}
      </div>
      {result.apply_stderr && (
        <pre className="text-xs bg-rose-100 text-rose-900 p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-32 font-mono">
          {result.apply_stderr}
        </pre>
      )}
      {tests.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-700 font-medium">
            Output test ({tests.length})
          </summary>
          <div className="mt-2 space-y-1">
            {tests.map((t, i) => (
              <div key={i} className={`p-2 rounded ${t.exit_code === 0 ? "bg-emerald-100" : "bg-rose-100"}`}>
                <div className="font-mono text-xs mb-1">
                  <strong>{t.exit_code === 0 ? "✓" : "✗"}</strong> {t.command}
                  {t.runtime_sec && <span className="ml-2 text-slate-500">({t.runtime_sec}s)</span>}
                </div>
                {(t.stdout || t.stderr) && (
                  <pre className="text-xs whitespace-pre-wrap max-h-24 overflow-y-auto">{(t.stdout || "") + (t.stderr || "")}</pre>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


function BudgetWidget({ budget, onClick }) {
  if (!budget) return null;
  const daily = budget.daily || {};
  const monthly = budget.monthly || {};
  const dPct = 100 - (daily.remaining_pct ?? 100);
  const mPct = 100 - (monthly.remaining_pct ?? 100);
  // Worst-of per il colore
  const worstPct = Math.max(dPct, mPct);
  const color = worstPct > 95 ? "rose" : worstPct > 80 ? "amber" : "emerald";
  return (
    <button data-testid="aisd-budget-widget"
             onClick={onClick}
             className={`flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-${color}-50 border-${color}-200 transition-colors`}>
      <Wallet className={`w-4 h-4 text-${color}-700`} />
      <div className="text-left">
        <div className="text-[10px] text-slate-500">Budget AI · oggi / mese</div>
        <div className="text-xs font-mono">
          <strong className={`text-${color}-700`}>€{(daily.spent_eur || 0).toFixed(2)}</strong>
          <span className="text-slate-400">/€{daily.cap_eur?.toFixed(0) ?? "15"}</span>
          <span className="text-slate-300 mx-1">·</span>
          <strong className={`text-${color}-700`}>€{(monthly.spent_eur || 0).toFixed(2)}</strong>
          <span className="text-slate-400">/€{monthly.cap_eur?.toFixed(0) ?? "30"}</span>
        </div>
      </div>
    </button>
  );
}

function ModeSettingsPanel({ mode, onUpdate }) {
  const [selectedMode, setSelectedMode] = useState(mode?.mode || "diagnose_only");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode?.mode) setSelectedMode(mode.mode);
  }, [mode]);

  if (!mode) return null;

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/mode`, { mode: selectedMode }, { withCredentials: true });
      toast.success(`Modalità → ${selectedMode}`);
      onUpdate && onUpdate();
    } catch (e) {
      toast.error("Salvataggio fallito");
    } finally { setBusy(false); }
  };

  const modes = [
    {
      id: "diagnose_only",
      label: "Diagnose Only",
      desc: "Solo analisi LLM. Nessuna proposta di fix.",
    },
    {
      id: "suggest_fix",
      label: "Suggest Fix",
      desc: "Analizza + genera proposta di fix navigabile (no auto-apply).",
    },
    {
      id: "safe_auto_fix",
      label: "Safe Auto-Fix",
      desc: "Riservato a FASE 7E. Applicherà fix nella whitelist (no auth/payment/db).",
      disabled: true,
    },
  ];

  return (
    <div data-testid="aisd-mode-settings"
          className="bg-white border border-slate-200 rounded-xl p-5">
      <h3 className="text-base font-semibold mb-3">Modalità AI Site Doctor</h3>
      <div className="space-y-2">
        {modes.map((m) => (
          <label key={m.id}
                  data-testid={`aisd-mode-radio-${m.id}`}
                  className={`flex items-start gap-2 p-3 rounded-lg border ${m.disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : "cursor-pointer hover:bg-slate-50"} ${selectedMode === m.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
            <input type="radio" name="aisd-mode" value={m.id}
                    checked={selectedMode === m.id}
                    disabled={m.disabled}
                    onChange={(e) => setSelectedMode(e.target.value)}
                    className="mt-1" />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-800">{m.label}</div>
              <div className="text-xs text-slate-500">{m.desc}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
        <span className="text-xs text-slate-500">
          {mode.last_modified_by ? (
            <>Ultima modifica: <strong>{mode.last_modified_by}</strong>
            {mode.last_modified_at && ` il ${new Date(mode.last_modified_at).toLocaleDateString("it-IT")}`}</>
          ) : "Mai modificato"}
        </span>
        <button data-testid="aisd-mode-save"
                disabled={busy || selectedMode === mode.mode}
                onClick={save}
                className="px-4 py-1.5 bg-emerald-700 text-white rounded-lg text-sm hover:bg-emerald-800 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
          Salva
        </button>
      </div>
    </div>
  );
}

function AutoAnalyzePanel({ autoAnalyze, onUpdate }) {
  const [busy, setBusy] = useState(false);
  if (!autoAnalyze) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auto-analyze`,
                       { enabled: !autoAnalyze.enabled },
                       { withCredentials: true });
      toast.success(autoAnalyze.enabled ? "Auto-analyze disattivato" : "Auto-analyze attivato");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="aisd-auto-analyze-panel"
          className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold">Auto-analyze on critical/high</h3>
          <p className="text-xs text-slate-500 mt-1">
            Analizza in background entro 1 min ogni errore critical/high. Throttle 1/min globale. Skip se budget &gt; 90%.
          </p>
        </div>
        <button data-testid="aisd-auto-analyze-toggle"
                disabled={busy}
                onClick={toggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${autoAnalyze.enabled ? "bg-emerald-600" : "bg-slate-300"}`}>
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${autoAnalyze.enabled ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Successi oggi</div>
          <div className="font-mono font-bold text-emerald-700">{autoAnalyze.success || 0}</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Skip budget</div>
          <div className="font-mono font-bold text-amber-700">{autoAnalyze.skipped_budget || 0}</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Skip throttle</div>
          <div className="font-mono font-bold text-slate-700">{autoAnalyze.skipped_throttle || 0}</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Costo oggi</div>
          <div className="font-mono font-bold">{(autoAnalyze.cost_today_eur || 0).toFixed(4)}€</div>
        </div>
      </div>
    </div>
  );
}

function SelfTestStepsPanel({ steps, onUpdate }) {
  const [active, setActive] = useState(steps?.current_active || []);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  useEffect(() => {
    if (steps?.current_active) setActive(steps.current_active);
  }, [steps]);

  if (!steps) return null;

  const toggle = (step) => {
    if (active.includes(step)) {
      setActive(active.filter((s) => s !== step));
    } else {
      setActive([...active, step]);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/selftest/steps`,
                       { selftest_steps: active }, { withCredentials: true });
      toast.success("Self-test steps aggiornati");
      onUpdate && onUpdate();
    } catch (e) {
      toast.error("Salvataggio fallito");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await axios.post(`${API}/selftest/run`, {},
                                          { withCredentials: true });
      setRunResult(data);
      if (data.passed) toast.success(`Self-test OK in ${data.total_runtime_sec}s`);
      else toast.error(`Self-test fallito allo step: ${data.failed_step}`);
    } catch (e) {
      toast.error("Run fallito");
    } finally { setRunning(false); }
  };

  return (
    <div data-testid="aisd-selftest-steps-panel"
          className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Self-Test Steps Attivi</h3>
          <p className="text-xs text-slate-500">
            Eseguiti automaticamente dopo ogni Apply per validare il fix.
          </p>
        </div>
        <button data-testid="aisd-selftest-run-now"
                disabled={running}
                onClick={runNow}
                className="px-3 py-1.5 bg-indigo-700 text-white rounded text-sm hover:bg-indigo-800 disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
          Esegui ora
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {steps.all_steps.map((s) => (
          <label key={s}
                  data-testid={`aisd-selftest-step-${s}`}
                  className="flex items-center gap-2 p-2 bg-slate-50 rounded cursor-pointer hover:bg-slate-100">
            <input type="checkbox"
                    checked={active.includes(s)}
                    onChange={() => toggle(s)} />
            <code className="text-xs">{s}</code>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-end">
        <button data-testid="aisd-selftest-save"
                disabled={busy}
                onClick={save}
                className="px-3 py-1.5 bg-emerald-700 text-white rounded text-sm hover:bg-emerald-800 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
          Salva
        </button>
      </div>

      {runResult && (
        <div data-testid="aisd-selftest-result"
              className={`mt-3 rounded-lg p-3 border ${runResult.passed ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
          <div className={`text-sm font-medium ${runResult.passed ? "text-emerald-900" : "text-rose-900"}`}>
            {runResult.passed ? "✓ Tutti gli step passati" : `✗ Failed at: ${runResult.failed_step}`}
            <span className="ml-2 text-xs text-slate-600">({runResult.total_runtime_sec}s)</span>
          </div>
          <details className="text-xs mt-2">
            <summary className="cursor-pointer text-slate-700">Output dettagliato</summary>
            <div className="mt-2 space-y-1">
              {(runResult.results || []).map((r, i) => (
                <div key={i} className={`p-2 rounded ${r.exit_code === 0 ? "bg-emerald-100" : "bg-rose-100"}`}>
                  <div className="font-mono">
                    <strong>{r.exit_code === 0 ? "✓" : "✗"}</strong> {r.name}
                    <span className="ml-2 text-slate-500">({r.runtime_sec}s)</span>
                  </div>
                  {(r.stdout || r.stderr) && (
                    <pre className="text-xs whitespace-pre-wrap max-h-24 overflow-y-auto mt-1">
                      {(r.stdout || "") + (r.stderr || "")}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function ApplicationsPanel({ data, onRefresh }) {
  if (!data) return null;
  const items = (data.items || []).slice(0, 20);
  const stats = data.stats || {};
  const counts = stats.counts || {};
  const successRate = stats.success_rate_pct || 0;
  const sColor = successRate >= 80 ? "emerald" : successRate >= 50 ? "amber" : "rose";
  return (
    <div data-testid="aisd-applications-panel"
          className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Storico Applicazioni</h3>
        <button data-testid="aisd-applications-refresh"
                onClick={onRefresh}
                className="text-xs text-emerald-700 hover:text-emerald-900">
          ⟳ Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Totale</div>
          <div className="font-mono font-bold">{stats.total || 0}</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Success rate</div>
          <div className={`font-mono font-bold text-${sColor}-700`}>{successRate}%</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Applied</div>
          <div className="font-mono font-bold text-emerald-700">{counts.applied || 0}</div>
        </div>
        <div className="bg-slate-50 rounded p-2">
          <div className="text-slate-500">Rolled back</div>
          <div className="font-mono font-bold text-amber-700">{counts.apply_failed_rolled_back || 0}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 text-center">
          Nessuna applicazione registrata. La storia apparirà dopo il primo apply.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-1">Quando</th>
                <th className="text-left py-1">fix_id</th>
                <th className="text-left py-1">Applier</th>
                <th className="text-left py-1">Modalità</th>
                <th className="text-left py-1">Status</th>
                <th className="text-right py-1">Tempo</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a, i) => {
                const c = a.final_status === "applied" ? "emerald"
                  : a.final_status === "apply_failed_rolled_back" ? "amber" : "rose";
                return (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5">{fmtDate(a.applied_at)}</td>
                    <td className="py-1.5 font-mono">{(a.fix_id || "").slice(0, 8)}…</td>
                    <td className="py-1.5">{a.applier_id || "—"}</td>
                    <td className="py-1.5">{a.auto_apply ? "Auto" : "Manual"}</td>
                    <td className="py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs bg-${c}-100 text-${c}-800`}>
                        {a.final_status}
                      </span>
                    </td>
                    <td className="py-1.5 text-right font-mono">{a.runtime_total_sec || 0}s</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-right">
        <a href="/admin/ai-site-doctor/applications"
            className="text-xs text-emerald-700 hover:underline">
          Vedi tutto →
        </a>
      </div>
    </div>
  );
}

function BudgetTab({ budget, mode, autoAnalyze, applications, selftestSteps, onUpdate, onModeUpdate, onAutoUpdate, onApplicationsRefresh, onSelftestRefresh }) {
  const [dailyCap, setDailyCap] = useState(15);
  const [monthlyCap, setMonthlyCap] = useState(30);
  const [autoStop, setAutoStop] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    if (budget) {
      setDailyCap(budget.daily?.cap_eur ?? budget.cap_eur ?? 15);
      setMonthlyCap(budget.monthly?.cap_eur ?? budget.cap_eur ?? 30);
      setAutoStop(budget.auto_stop_on_cap !== false);
    }
  }, [budget]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/budget/history?days=30`, { withCredentials: true });
      setHistory(data?.items || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  if (!budget) return <Loader2 className="w-6 h-6 animate-spin mx-auto" />;

  const save = async () => {
    if (parseFloat(dailyCap) > parseFloat(monthlyCap)) {
      toast.error("Il cap giornaliero non può superare quello mensile");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/budget/config`, {
        daily_cap_eur: parseFloat(dailyCap),
        monthly_cap_eur: parseFloat(monthlyCap),
        auto_stop_on_cap: autoStop,
      }, { withCredentials: true });
      toast.success("Budget aggiornato");
      onUpdate && onUpdate();
      loadHistory();
    } catch (e) {
      toast.error("Salvataggio fallito: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const breakdown = Object.entries(budget.model_breakdown || {});
  const daily = budget.daily || { cap_eur: 15, spent_eur: 0, remaining_pct: 100 };
  const monthly = budget.monthly || { cap_eur: 30, spent_eur: 0, remaining_pct: 100 };
  const dailyPct = 100 - (daily.remaining_pct || 0);
  const monthlyPct = 100 - (monthly.remaining_pct || 0);
  const dailyColor = dailyPct >= 90 ? "rose" : dailyPct >= 50 ? "amber" : "emerald";
  const monthlyColor = monthlyPct >= 90 ? "rose" : monthlyPct >= 50 ? "amber" : "emerald";

  // Onboarding banner se mode === diagnose_only
  const isDiagnoseOnly = (mode?.mode || "diagnose_only") === "diagnose_only";

  // Grafico history: barre per giorno (max ultimi 30)
  const maxSpend = Math.max(0.001, ...history.map(h => h.spent_eur || 0));

  return (
    <div data-testid="aisd-budget-tab" className="space-y-4">
      {isDiagnoseOnly && (
        <div data-testid="aisd-fix-mode-banner"
              className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-sky-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sky-900">AI Site Doctor è in modalità solo diagnosi</div>
            <div className="text-xs text-sky-700">Per attivare i fix automatici (con o senza approvazione), configura la modalità Fix.</div>
          </div>
          <button data-testid="aisd-activate-fix-mode-btn"
                  onClick={() => setShowWizard(true)}
                  className="px-4 py-2 bg-sky-700 text-white rounded-lg text-sm font-semibold hover:bg-sky-800">
            Configura Fix Mode
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div data-testid="aisd-budget-daily-widget"
              className={`bg-white border border-${dailyColor}-200 rounded-xl p-5`}>
          <div className="flex items-center gap-2 mb-2">
            <Wallet className={`w-5 h-5 text-${dailyColor}-700`} />
            <h3 className="text-sm font-semibold">Spesa OGGI ({daily.date})</h3>
          </div>
          <div className="flex items-end gap-2 mb-2">
            <span className={`text-3xl font-bold text-${dailyColor}-700`}>
              €{(daily.spent_eur || 0).toFixed(4)}
            </span>
            <span className="text-slate-400 text-sm mb-1">/ €{daily.cap_eur.toFixed(2)}</span>
            <span className={`ml-auto text-xs font-mono text-${dailyColor}-700`}>{dailyPct.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2">
            <div className={`h-2 bg-${dailyColor}-500 rounded-full transition-all`}
                  style={{ width: `${Math.min(100, dailyPct)}%` }} />
          </div>
          <div className="text-xs text-slate-500 mt-2">📊 {daily.requests_count || 0} richieste oggi</div>
        </div>

        <div data-testid="aisd-budget-monthly-widget"
              className={`bg-white border border-${monthlyColor}-200 rounded-xl p-5`}>
          <div className="flex items-center gap-2 mb-2">
            <Wallet className={`w-5 h-5 text-${monthlyColor}-700`} />
            <h3 className="text-sm font-semibold">Spesa MESE ({monthly.month})</h3>
          </div>
          <div className="flex items-end gap-2 mb-2">
            <span className={`text-3xl font-bold text-${monthlyColor}-700`}>
              €{(monthly.spent_eur || 0).toFixed(4)}
            </span>
            <span className="text-slate-400 text-sm mb-1">/ €{monthly.cap_eur.toFixed(2)}</span>
            <span className={`ml-auto text-xs font-mono text-${monthlyColor}-700`}>{monthlyPct.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2">
            <div className={`h-2 bg-${monthlyColor}-500 rounded-full transition-all`}
                  style={{ width: `${Math.min(100, monthlyPct)}%` }} />
          </div>
          <div className="text-xs text-slate-500 mt-2">📊 {monthly.requests_count || 0} richieste questo mese</div>
        </div>
      </div>

      {history.length > 0 && (
        <div data-testid="aisd-budget-history"
              className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold mb-3">Spesa per giorno (ultimi {history.length})</h3>
          <div className="flex items-end gap-1 h-24 overflow-x-auto">
            {history.slice().reverse().map((h) => {
              const heightPct = ((h.spent_eur || 0) / maxSpend) * 100;
              const barColor = h.spent_eur >= daily.cap_eur ? "bg-rose-500" :
                                h.spent_eur >= daily.cap_eur * 0.5 ? "bg-amber-500" : "bg-emerald-500";
              return (
                <div key={h.date} className="flex flex-col items-center" style={{ minWidth: 18 }}>
                  <div className={`w-3 ${barColor} rounded-t`}
                        style={{ height: `${Math.max(2, heightPct)}%` }}
                        title={`${h.date}: €${h.spent_eur.toFixed(4)} (${h.requests_count} req)`} />
                  <div className="text-[8px] text-slate-400 mt-1 transform -rotate-45 origin-top-left">
                    {h.date.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-base font-semibold mb-3">Breakdown per modello (mese)</h3>
        {breakdown.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Nessun utilizzo questo mese</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-1">Modello</th>
                <th className="text-right py-1">Costo</th>
                <th className="text-right py-1">% mese</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map(([model, cost]) => (
                <tr key={model} className="border-b border-slate-100">
                  <td className="py-1.5 font-mono text-xs">{model}</td>
                  <td className="py-1.5 text-right font-mono">€{cost.toFixed(5)}</td>
                  <td className="py-1.5 text-right text-slate-500">{((cost / Math.max(monthly.spent_eur, 0.0001)) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-base font-semibold mb-3">Configurazione cap (dual)</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Cap GIORNALIERO (€{dailyCap})
            </label>
            <input data-testid="aisd-budget-daily-cap-slider"
                    type="range" min="1" max="100" step="0.5"
                    value={dailyCap}
                    onChange={(e) => setDailyCap(e.target.value)}
                    className="w-full" />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>€1</span><span>€15 (cliente)</span><span>€50</span><span>€100</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Cap MENSILE (€{monthlyCap})
            </label>
            <input data-testid="aisd-budget-monthly-cap-slider"
                    type="range" min="1" max="100" step="0.5"
                    value={monthlyCap}
                    onChange={(e) => setMonthlyCap(e.target.value)}
                    className="w-full" />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>€1</span><span>€30 (cliente)</span><span>€50</span><span>€100</span>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            ℹ️ Hard-stop su qualunque dei due raggiunto. Il giornaliero NON può superare il mensile.
          </p>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="aisd-budget-autostop"
                    type="checkbox" checked={autoStop}
                    onChange={(e) => setAutoStop(e.target.checked)}
                    className="w-4 h-4" />
            <span>Auto-stop quando uno dei due cap viene superato</span>
          </label>
          <button data-testid="aisd-budget-save"
                  disabled={busy}
                  onClick={save}
                  className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm hover:bg-emerald-800 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
            {" "}Salva configurazione
          </button>
        </div>
      </div>

      <ModeSettingsPanel mode={mode} onUpdate={onModeUpdate} />
      <AutoAnalyzePanel autoAnalyze={autoAnalyze} onUpdate={onAutoUpdate} />
      <PatternSavingsCard />
      <SelfTestStepsPanel steps={selftestSteps} onUpdate={onSelftestRefresh} />
      <ApplicationsPanel data={applications} onRefresh={onApplicationsRefresh} />

      <FixModeWizard open={showWizard} onClose={() => setShowWizard(false)}
                       mode={mode} onUpdate={onModeUpdate} />
    </div>
  );
}


// ============================================================================
// v1.14.76.1 — Fix Mode Wizard (onboarding 3-step)
// ============================================================================
function FixModeWizard({ open, onClose, mode, onUpdate }) {
  const [step, setStep] = useState(1);
  const [chosenMode, setChosenMode] = useState("suggest_fix");
  const [whitelist, setWhitelist] = useState(["retry", "regenerate_pdf", "clear_cache"]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setChosenMode(mode?.mode || "suggest_fix");
      setWhitelist(mode?.safe_auto_fix_whitelist || ["retry", "regenerate_pdf", "clear_cache"]);
    }
  }, [open, mode]);

  if (!open) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/mode`, {
        mode: chosenMode,
        safe_auto_fix_whitelist: whitelist,
      }, { withCredentials: true });
      toast.success(`Modalità impostata su: ${chosenMode}`);
      onUpdate && onUpdate();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const toggleCategory = (cat) => {
    setWhitelist(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  return (
    <div data-testid="aisd-fix-mode-wizard"
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full my-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Configura Fix Mode · Step {step}/3</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5">
          {step === 1 && (
            <div data-testid="aisd-wizard-step-1" className="space-y-3 text-sm text-slate-700">
              <p>AI Site Doctor può operare in <strong>3 modalità</strong>:</p>
              <div className="border border-slate-200 rounded-lg p-3">
                <strong className="text-slate-900">🔍 Diagnose Only</strong> <span className="text-xs text-slate-500">(default attuale)</span>
                <p className="text-xs mt-1">Analizza gli errori con LLM ma NON propone né applica fix. Massima sicurezza.</p>
              </div>
              <div className="border border-emerald-200 bg-emerald-50/30 rounded-lg p-3">
                <strong className="text-emerald-900">💡 Suggest Fix</strong>
                <p className="text-xs mt-1">Analizza + propone fix. Admin approva manualmente prima dell&apos;apply. <strong>Raccomandato.</strong></p>
              </div>
              <div className="border border-amber-200 bg-amber-50/30 rounded-lg p-3">
                <strong className="text-amber-900">⚡ Safe Auto-Fix</strong>
                <p className="text-xs mt-1">Applica automaticamente fix nella whitelist (es: retry, regenerate_pdf). Per categorie sensibili serve sempre approvazione admin. <strong>Solo se hai confidenza nel sistema.</strong></p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                ℹ️ Ogni fix è soggetto a safety checks (path blacklist, pattern critici, diff valido) e auto-rollback se i test post-apply falliscono.
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="aisd-wizard-step-2" className="space-y-4">
              <p className="text-sm font-semibold">Scegli la modalità:</p>
              {[
                ["diagnose_only", "🔍 Diagnose Only", "Solo analisi LLM"],
                ["suggest_fix", "💡 Suggest Fix", "Analisi + proposte (raccomandato)"],
                ["safe_auto_fix", "⚡ Safe Auto-Fix", "Apply automatico whitelist"],
              ].map(([k, label, desc]) => (
                <label key={k} data-testid={`aisd-wizard-mode-${k}`}
                        className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${chosenMode === k ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <input type="radio" name="mode" checked={chosenMode === k}
                          onChange={() => setChosenMode(k)} className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm">{label}</div>
                    <div className="text-xs text-slate-500">{desc}</div>
                  </div>
                </label>
              ))}

              {chosenMode === "safe_auto_fix" && (
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-sm font-semibold mb-2">Categorie auto-fix abilitate (whitelist):</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {["retry", "regenerate_pdf", "clear_cache", "null_check",
                       "input_validation", "code_change", "schema_change"].map(cat => (
                      <label key={cat} data-testid={`aisd-wizard-cat-${cat}`}
                              className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={whitelist.includes(cat)}
                                onChange={() => toggleCategory(cat)} />
                        <span>{cat}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    ⚠️ Le categorie sensibili (code_change, schema_change) richiederanno sempre approvazione admin.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="aisd-wizard-step-3" className="space-y-3">
              <p className="text-sm">Conferma le impostazioni:</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <div><strong>Modalità:</strong> <code>{chosenMode}</code></div>
                {chosenMode === "safe_auto_fix" && (
                  <div className="mt-1"><strong>Whitelist:</strong> {whitelist.join(", ")}</div>
                )}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                ⚠️ Cambio modalità verrà loggato nell&apos;audit. Puoi cambiarlo in qualsiasi momento.
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                  className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-100">
            {step > 1 ? "Indietro" : "Annulla"}
          </button>
          {step < 3 ? (
            <button data-testid="aisd-wizard-next"
                    onClick={() => setStep(step + 1)}
                    className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm font-semibold hover:bg-emerald-800">
              Continua
            </button>
          ) : (
            <button data-testid="aisd-wizard-confirm"
                    onClick={confirm} disabled={busy}
                    className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null}
              {" "}Conferma e attiva
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function DetailModal({ detail, onClose, onStatusChange, onAnalyzed, mode }) {
  const [busy, setBusy] = useState(false);
  if (!detail) return null;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copiato"),
      () => toast.error("Copia fallita"));
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await onStatusChange(detail.id, status);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="aisd-detail-modal"
         className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
         onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${SEVERITY_BADGE[detail.severity]}`}>{detail.severity}</span>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_BADGE[detail.source]}`}>{detail.source}</span>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[detail.status]}`}>{detail.status}</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Dettaglio errore</h3>
          </div>
          <button data-testid="aisd-modal-close"
                  onClick={onClose}
                  className="p-1.5 hover:bg-slate-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <DetailSection title="Contesto">
            <DetailField label="ID" value={detail.id} mono />
            <DetailField label="Page URL" value={detail.page_url} />
            <DetailField label="Component" value={detail.component_name} />
            <DetailField label="Browser" value={detail.browser} />
            <DetailField label="Frontend version" value={detail.frontend_version} />
          </DetailSection>

          <DetailSection title="Errore">
            <DetailField label="Messaggio" value={detail.error_message} />
            {detail.stack_trace && (
              <div className="mt-2">
                <span className="text-xs text-slate-500">Stack trace</span>
                <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded-lg overflow-x-auto mt-1 font-mono whitespace-pre-wrap break-all max-h-64">
                  {detail.stack_trace}
                </pre>
              </div>
            )}
            <DetailField label="Occorrenze" value={`${detail.occurrences_count || 1}× (ultima: ${fmtDate(detail.last_seen_at)})`} />
          </DetailSection>

          {(detail.api_endpoint || detail.api_status_code) && (
            <DetailSection title="API">
              <DetailField label="Endpoint" value={detail.api_endpoint} mono />
              <DetailField label="Method" value={detail.api_method} />
              <DetailField label="Status code" value={detail.api_status_code} />
              {detail.request_payload_sanitized && Object.keys(detail.request_payload_sanitized).length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer text-slate-600 hover:text-slate-900">Request payload (sanitized)</summary>
                  <pre className="bg-slate-50 border border-slate-200 text-xs p-3 rounded-lg overflow-x-auto mt-1 font-mono">
                    {JSON.stringify(detail.request_payload_sanitized, null, 2)}
                  </pre>
                </details>
              )}
              {detail.response_payload_sanitized && Object.keys(detail.response_payload_sanitized).length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer text-slate-600 hover:text-slate-900">Response payload (sanitized)</summary>
                  <pre className="bg-slate-50 border border-slate-200 text-xs p-3 rounded-lg overflow-x-auto mt-1 font-mono">
                    {JSON.stringify(detail.response_payload_sanitized, null, 2)}
                  </pre>
                </details>
              )}
            </DetailSection>
          )}

          {detail.dom_error_text && (
            <DetailSection title="DOM (red message)">
              <DetailField label="Testo" value={detail.dom_error_text} />
            </DetailSection>
          )}

          <DetailSection title="Trace">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 w-36 flex-shrink-0">Correlation ID</span>
              <span className="text-xs font-mono text-slate-800">{detail.correlation_id || "—"}</span>
              {detail.correlation_id && (
                <button onClick={() => copyToClipboard(detail.correlation_id)}
                        className="p-1 hover:bg-slate-100 rounded">
                  <Copy className="w-3.5 h-3.5 text-slate-500" />
                </button>
              )}
            </div>
            <DetailField label="Session ID" value={detail.session_id} mono />
            <DetailField label="User ID" value={detail.user_id} mono />
            <DetailField label="Signature" value={detail.error_signature} mono />
          </DetailSection>

          {detail.action_before_error && detail.action_before_error.length > 0 && (
            <DetailSection title="Azioni prima dell'errore">
              <pre className="bg-slate-50 border border-slate-200 text-xs p-3 rounded-lg overflow-x-auto font-mono max-h-48">
                {JSON.stringify(detail.action_before_error, null, 2)}
              </pre>
            </DetailSection>
          )}

          <DetailSection title="AI Analysis (FASE 7C)">
            <AIAnalysisPanel detail={detail} onAnalyzed={onAnalyzed} />
          </DetailSection>

          <DetailSection title="Fix Suggerito (FASE 7D)">
            <SuggestFixPanel detail={detail} mode={mode} />
          </DetailSection>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex flex-wrap gap-2 justify-end">
          <button data-testid="aisd-export-json-btn"
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(detail, null, 2)],
                                          { type: "application/json" });
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = `error-${detail.id.slice(0, 8)}.json`;
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
                  }}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-100">
            Esporta JSON
          </button>
          {detail.status !== "ignored" && (
            <button data-testid="aisd-status-ignore-btn"
                    disabled={busy}
                    onClick={() => changeStatus("ignored")}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-50">
              Marca come Ignorato
            </button>
          )}
          {detail.status !== "investigating" && (
            <button data-testid="aisd-status-investigate-btn"
                    disabled={busy}
                    onClick={() => changeStatus("investigating")}
                    className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50">
              In analisi
            </button>
          )}
          {detail.status !== "fixed" && (
            <button data-testid="aisd-status-fixed-btn"
                    disabled={busy}
                    onClick={() => changeStatus("fixed")}
                    className="px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm hover:bg-emerald-800 disabled:opacity-50">
              Marca come Risolto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// v1.14.76.0 — FASE 7G: Health & Notifications Tab
// ============================================================================
const HEALTH_BADGE = {
  green:   "bg-emerald-100 text-emerald-800 border-emerald-300",
  yellow:  "bg-amber-100 text-amber-800 border-amber-300",
  red:     "bg-red-100 text-red-800 border-red-300",
  unknown: "bg-slate-100 text-slate-600 border-slate-300",
};

const CHECK_LABEL_IT = {
  backend_reachable:    "Backend raggiungibile",
  mongo_latency:        "Latenza MongoDB",
  storage_disk:         "Disco / Storage",
  storage_files:        "File generati (PDF/backup)",
  pdf_gen_smoke:        "Pipeline PDF",
  env_vars:             "Variabili ambiente",
  llm_budget_remaining: "Budget LLM residuo",
  error_rate_15m:       "Tasso errori (15min)",
  failed_applies_24h:   "Apply falliti (24h)",
};

function HealthCheckCard({ check }) {
  const sev = check.status || "unknown";
  const label = CHECK_LABEL_IT[check.name] || check.name;
  return (
    <div data-testid={`aisd-health-card-${check.name}`}
          className={`rounded-xl border p-4 ${HEALTH_BADGE[sev]}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-white/60">
          {sev}
        </span>
      </div>
      <div className="text-xs opacity-80 mb-1">{check.message || "—"}</div>
      <div className="text-[11px] opacity-70 font-mono break-all">
        {typeof check.value === "object" && check.value !== null
          ? JSON.stringify(check.value).slice(0, 80)
          : (check.value !== null && check.value !== undefined ? String(check.value) : "—")}
        {check.threshold !== null && check.threshold !== undefined && (
          <span className="opacity-60"> · soglia {String(check.threshold)}</span>
        )}
      </div>
    </div>
  );
}

function NotifConfigModal({ open, onClose, channel, config, onSave }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);
  useEffect(() => { if (open) setForm({}); }, [open]);
  if (!open) return null;
  const isEmail = channel === "email";
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (isEmail) {
        if (form.email_api_key && !form.email_api_key.includes("•")) payload.email_api_key = form.email_api_key;
        if (form.email_provider) payload.email_provider = form.email_provider;
        if (form.email_from) payload.email_from = form.email_from;
        if (form.email_to_admin !== undefined) {
          payload.email_to_admin = form.email_to_admin.split(",").map(s => s.trim()).filter(Boolean);
        }
      } else {
        if (form.telegram_bot_token && !form.telegram_bot_token.includes("•")) payload.telegram_bot_token = form.telegram_bot_token;
        if (form.telegram_chat_id) payload.telegram_chat_id = form.telegram_chat_id;
      }
      await onSave(payload);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`aisd-notif-modal-${channel}`}
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">
            Configura {isEmail ? "Email" : "Telegram"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        {isEmail ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Provider</label>
              <select data-testid="aisd-notif-email-provider"
                      defaultValue={config?.email_provider || "resend"}
                      onChange={(e) => setForm({...form, email_provider: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="resend">Resend (100/giorno gratis)</option>
                <option value="sendgrid">SendGrid</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                API Key {config?.email_api_key_masked && <span className="text-emerald-600">(attiva: {config.email_api_key_masked})</span>}
              </label>
              <div className="relative">
                <input data-testid="aisd-notif-email-api-key"
                        type={showKey ? "text" : "password"}
                        placeholder="re_xxxxxxxxxx"
                        onChange={(e) => setForm({...form, email_api_key: e.target.value})}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm pr-10" />
                <button type="button" onClick={() => setShowKey(s => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                Ottieni la chiave su <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">resend.com/api-keys</a>
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Email mittente (From)</label>
              <input data-testid="aisd-notif-email-from"
                      type="email" placeholder="no-reply@colucciassicuratori.com"
                      defaultValue={config?.email_from}
                      onChange={(e) => setForm({...form, email_from: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Destinatari admin (separati da virgola)</label>
              <input data-testid="aisd-notif-email-to"
                      type="text" placeholder="admin@colucci.local, sviluppo@colucci.local"
                      defaultValue={(config?.email_to_admin || []).join(", ")}
                      onChange={(e) => setForm({...form, email_to_admin: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Bot Token {config?.telegram_bot_token_masked && <span className="text-emerald-600">(attivo: {config.telegram_bot_token_masked})</span>}
              </label>
              <input data-testid="aisd-notif-telegram-token"
                      type={showKey ? "text" : "password"}
                      placeholder="123456789:ABCdefGHI..."
                      onChange={(e) => setForm({...form, telegram_bot_token: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Chat ID</label>
              <input data-testid="aisd-notif-telegram-chat"
                      type="text" placeholder="-100xxxxxxxxxx oppure xxxxxxxx"
                      defaultValue={config?.telegram_chat_id}
                      onChange={(e) => setForm({...form, telegram_chat_id: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              <strong>Come ottenere bot_token e chat_id:</strong>
              <ol className="list-decimal pl-4 mt-1 space-y-0.5">
                <li>Apri Telegram e parla con <code>@BotFather</code></li>
                <li>Invia <code>/newbot</code> e segui le istruzioni → otterrai il bot_token</li>
                <li>Aggiungi il bot a un gruppo o cercalo come utente</li>
                <li>Invia un messaggio al bot</li>
                <li>Apri <code>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> → cerca <code>chat.id</code></li>
              </ol>
            </div>
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50">
            Annulla
          </button>
          <button data-testid={`aisd-notif-modal-save-${channel}`}
                  onClick={save} disabled={busy}
                  className="flex-1 px-3 py-2 bg-emerald-700 text-white rounded-lg text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : "Salva configurazione"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HealthNotificationsTab() {
  const [snapshot, setSnapshot] = useState(null);
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [subtab, setSubtab] = useState("salute");
  const [modal, setModal] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [snapRes, histRes, cfgRes, logsRes] = await Promise.all([
        axios.get(`${API}/health`, { withCredentials: true }),
        axios.get(`${API}/health/history?hours=24`, { withCredentials: true }),
        axios.get(`${API}/notifications/config`, { withCredentials: true }),
        axios.get(`${API}/notifications/log?limit=50`, { withCredentials: true }),
      ]);
      setSnapshot(snapRes.data);
      setHistory(histRes.data?.items || []);
      setConfig(cfgRes.data);
      setLogs(logsRes.data?.items || []);
    } catch (e) {
      toast.error("Errore caricamento Salute & Notifiche");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/health/run-now`, {}, { withCredentials: true });
      setSnapshot(data);
      toast.success(`Health check eseguito: ${data.overall_status}`);
      await loadAll();
    } catch (e) {
      toast.error("Errore esecuzione health check");
    } finally { setRunning(false); }
  };

  const saveConfig = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/notifications/config`, payload, { withCredentials: true });
      setConfig(data);
      toast.success("Configurazione salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const updateTrigger = async (triggerName, patch) => {
    const newTriggers = { ...(config?.triggers || {}) };
    newTriggers[triggerName] = { ...newTriggers[triggerName], ...patch };
    await saveConfig({ triggers: newTriggers });
  };

  const testChannel = async (channel) => {
    try {
      const url = channel === "email" ? "/notifications/test-email" : "/notifications/test-telegram";
      const { data } = await axios.post(`${API}${url}`, {}, { withCredentials: true });
      if (data?.success) toast.success(`Test ${channel} inviato`);
      else toast.error(`Test ${channel} fallito: ${data?.message || "errore"}`);
      await loadAll();
    } catch (e) {
      toast.error(`Errore test ${channel}`);
    }
  };

  const overall = snapshot?.overall_status || "unknown";
  const checks = snapshot?.checks || [];
  const overallColor = HEALTH_BADGE[overall];

  return (
    <div data-testid="aisd-health-notifications-tab" className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="aisd-subtab-salute"
                onClick={() => setSubtab("salute")}
                className={`px-3 py-1.5 text-sm rounded-lg border ${subtab === "salute" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <HeartPulse className="w-4 h-4 inline mr-1" /> Salute Sistema
        </button>
        <button data-testid="aisd-subtab-notifiche"
                onClick={() => setSubtab("notifiche")}
                className={`px-3 py-1.5 text-sm rounded-lg border ${subtab === "notifiche" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Bell className="w-4 h-4 inline mr-1" /> Notifiche
        </button>
      </div>

      {subtab === "salute" && (
        <>
          <div data-testid="aisd-health-overall"
                className={`flex items-center justify-between rounded-xl border p-4 ${overallColor}`}>
            <div>
              <div className="text-xs uppercase font-semibold opacity-70">Stato complessivo</div>
              <div className="text-3xl font-bold mt-1">
                {overall === "green" && "🟢 Tutto OK"}
                {overall === "yellow" && "🟡 Attenzione"}
                {overall === "red" && "🔴 Critico"}
                {overall === "unknown" && "⚪ Sconosciuto"}
              </div>
              <div className="text-xs opacity-70 mt-1">
                Snapshot {snapshot?.snapshot_id || "—"} · {snapshot?.created_at ? fmtDate(snapshot.created_at) : "—"}
              </div>
            </div>
            <button data-testid="aisd-health-runnow"
                    onClick={runNow} disabled={running}
                    className="px-4 py-2 bg-white border border-slate-300 text-slate-800 rounded-lg text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
              {running ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <RefreshCw className="w-4 h-4 inline" />}
              {" "}Esegui Ora
            </button>
          </div>

          <div data-testid="aisd-health-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {checks.map((c) => <HealthCheckCard key={c.name} check={c} />)}
          </div>

          <div data-testid="aisd-health-history"
                className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900">Storico ultimi 24h ({history.length} snapshot)</h3>
            </div>
            <div className="flex items-end gap-1 h-12 overflow-x-auto">
              {history.slice().reverse().map((h, i) => {
                const color = h.overall_status === "green" ? "bg-emerald-500" :
                               h.overall_status === "yellow" ? "bg-amber-500" :
                               h.overall_status === "red" ? "bg-red-500" : "bg-slate-300";
                return <div key={i} className={`w-2 ${color} rounded-sm`} style={{ height: "100%" }} title={`${h.overall_status} · ${fmtDate(h.created_at)}`} />;
              })}
              {history.length === 0 && (
                <div className="text-xs text-slate-400 italic">Nessuno snapshot ancora. Lo scheduler gira ogni 5 minuti.</div>
              )}
            </div>
          </div>
        </>
      )}

      {subtab === "notifiche" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div data-testid="aisd-channel-email"
                  className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-emerald-700" />
                  <span className="font-bold text-slate-900">Email</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${config?.email_api_key_masked ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                  {config?.email_api_key_masked ? "ATTIVA" : "INATTIVA"}
                </span>
              </div>
              <div className="text-xs text-slate-600 mb-3">
                {config?.email_api_key_masked
                  ? `Provider: ${config.email_provider} · Key: ${config.email_api_key_masked}`
                  : "Email non configurata. Provider supportati: Resend, SendGrid."}
              </div>
              <div className="flex gap-2">
                <button data-testid="aisd-config-email-btn"
                        onClick={() => setModal("email")}
                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50">
                  Configura
                </button>
                <button data-testid="aisd-test-email-btn"
                        onClick={() => testChannel("email")}
                        disabled={!config?.email_api_key_masked}
                        className="flex-1 px-3 py-1.5 bg-emerald-700 text-white rounded-lg text-xs font-semibold hover:bg-emerald-800 disabled:opacity-30">
                  <Send className="w-3 h-3 inline mr-1" /> Test
                </button>
              </div>
            </div>

            <div data-testid="aisd-channel-telegram"
                  className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-sky-700" />
                  <span className="font-bold text-slate-900">Telegram</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${config?.telegram_bot_token_masked ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                  {config?.telegram_bot_token_masked ? "ATTIVO" : "INATTIVO"}
                </span>
              </div>
              <div className="text-xs text-slate-600 mb-3">
                {config?.telegram_bot_token_masked
                  ? `Bot: ${config.telegram_bot_token_masked} · Chat: ${config.telegram_chat_id || "—"}`
                  : "Telegram non configurato. Servono bot_token (da @BotFather) e chat_id."}
              </div>
              <div className="flex gap-2">
                <button data-testid="aisd-config-telegram-btn"
                        onClick={() => setModal("telegram")}
                        className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50">
                  Configura
                </button>
                <button data-testid="aisd-test-telegram-btn"
                        onClick={() => testChannel("telegram")}
                        disabled={!config?.telegram_bot_token_masked}
                        className="flex-1 px-3 py-1.5 bg-sky-700 text-white rounded-lg text-xs font-semibold hover:bg-sky-800 disabled:opacity-30">
                  <Send className="w-3 h-3 inline mr-1" /> Test
                </button>
              </div>
            </div>
          </div>

          <div data-testid="aisd-triggers-panel"
                className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3">Trigger automatici</h3>
            <div className="space-y-2">
              {[
                ["critical_error",   "Errore critico rilevato"],
                ["apply_failed",     "Apply fix fallito (rollback automatico)"],
                ["manual_rollback",  "Rollback manuale eseguito"],
                ["health_red",       "Health check passa a ROSSO"],
                ["budget_threshold", "Budget LLM oltre 80% / 95%"],
                ["daily_digest",     "Digest giornaliero (mattina)"],
              ].map(([key, label]) => {
                const t = (config?.triggers || {})[key] || { enabled: false, channel: "email" };
                return (
                  <div key={key} data-testid={`aisd-trigger-${key}`}
                        className="flex items-center justify-between border-b border-slate-100 last:border-b-0 py-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{label}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{key}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select data-testid={`aisd-trigger-${key}-channel`}
                              value={t.channel}
                              onChange={(e) => updateTrigger(key, { channel: e.target.value })}
                              className="text-xs border border-slate-200 rounded px-2 py-1">
                        <option value="email">Email</option>
                        <option value="telegram">Telegram</option>
                        <option value="both">Entrambi</option>
                      </select>
                      <button data-testid={`aisd-trigger-${key}-toggle`}
                              onClick={() => updateTrigger(key, { enabled: !t.enabled })}
                              role="switch" aria-checked={t.enabled}
                              className={`w-10 h-5 rounded-full relative transition ${t.enabled ? "bg-emerald-600" : "bg-slate-300"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition ${t.enabled ? "left-5" : "left-0.5"}`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div data-testid="aisd-notif-log"
                className="bg-white border border-slate-200 rounded-xl p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3">Storico notifiche inviate (ultime 50)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Quando</th>
                    <th className="px-2 py-1.5 text-left">Trigger</th>
                    <th className="px-2 py-1.5 text-left">Canale</th>
                    <th className="px-2 py-1.5 text-left">Esito</th>
                    <th className="px-2 py-1.5 text-left">Oggetto</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 && (
                    <tr><td colSpan={5} className="py-6 text-center text-slate-400 italic">Nessuna notifica ancora inviata</td></tr>
                  )}
                  {logs.map((l, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-[10px]">{fmtDate(l.sent_at)}</td>
                      <td className="px-2 py-1.5">{l.trigger}</td>
                      <td className="px-2 py-1.5">{l.channel}</td>
                      <td className="px-2 py-1.5">
                        {l.success
                          ? <span className="text-emerald-700 font-semibold">✓ inviata</span>
                          : <span className="text-red-700 font-semibold">✗ fallita</span>}
                      </td>
                      <td className="px-2 py-1.5 truncate max-w-[200px]">{l.subject || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <NotifConfigModal open={modal === "email"} onClose={() => setModal(null)}
                          channel="email" config={config} onSave={saveConfig} />
      <NotifConfigModal open={modal === "telegram"} onClose={() => setModal(null)}
                          channel="telegram" config={config} onSave={saveConfig} />
    </div>
  );
}


function PatternSavingsCard() {
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          axios.get(`${API}/patterns/stats`, { withCredentials: true }),
          axios.get(`${API}/patterns/savings-trend?weeks=12`, { withCredentials: true }),
        ]);
        setStats(s.data);
        setTrend(t.data?.items || []);
      } catch (e) { /* silent */ }
    })();
  }, []);
  if (!stats) return null;
  const maxApplies = Math.max(1, ...trend.map(t => t.applies || 0));
  return (
    <div data-testid="aisd-pattern-savings-card"
          className="bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-base font-bold text-slate-900">🧬 Risparmio Pattern Library</h3>
          <p className="text-xs text-slate-600">Chiamate LLM evitate grazie ai pattern testati</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-emerald-700">€{(stats.estimated_eur_saved || 0).toFixed(4)}</div>
          <div className="text-[10px] text-slate-500">{stats.total_llm_calls_saved} chiamate evitate · {stats.active} pattern attivi</div>
        </div>
      </div>
      {trend.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] text-slate-500 mb-1">Trend ultime 12 settimane</div>
          <div className="flex items-end gap-1 h-12">
            {trend.map(t => (
              <div key={t.week} className="flex flex-col items-center flex-1 min-w-[24px]">
                <div className="w-full bg-emerald-500 rounded-t"
                      style={{ height: `${(t.applies / maxApplies) * 100}%`, minHeight: "2px" }}
                      title={`${t.week}: ${t.applies} apply (€${t.estimated_eur_saved.toFixed(4)})`} />
                <div className="text-[8px] text-slate-400 mt-1">{t.week.slice(-3)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ============================================================================
// v1.14.77.0 — FASE 7H: Pattern Library Tab
// ============================================================================
const PATTERN_STATUS_COLOR = {
  active:   "bg-emerald-100 text-emerald-800 border-emerald-300",
  retired:  "bg-slate-200 text-slate-600 border-slate-300",
  disabled: "bg-amber-100 text-amber-800 border-amber-300",
};

function PatternDetailModal({ pattern, onClose, onRetire, onReactivate }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [actionType, setActionType] = useState(null);
  if (!pattern) return null;

  const execute = async () => {
    if (reason.length < 10) { toast.error("Reason ≥ 10 caratteri"); return; }
    setBusy(true);
    try {
      if (actionType === "retire") await onRetire(pattern.pattern_id, reason);
      else await onReactivate(pattern.pattern_id, reason);
      onClose();
    } finally { setBusy(false); }
  };

  const outcomes = pattern.last_known_outcomes || [];

  return (
    <div data-testid="aisd-pattern-detail-modal"
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full my-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">🧬 Pattern {pattern.pattern_id.slice(0, 8)}...</h3>
            <p className="text-xs text-slate-500 font-mono">{pattern.fix_category} · {pattern.file_path_pattern}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] text-slate-500 uppercase">Applicazioni</div>
              <div className="text-xl font-bold">{pattern.applications_count || 0}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] text-slate-500 uppercase">Success Rate</div>
              <div className="text-xl font-bold">{((pattern.success_rate || 0) * 100).toFixed(0)}%</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] text-slate-500 uppercase">Confidence</div>
              <div className="text-xl font-bold">{((pattern.confidence_score || 0) * 100).toFixed(0)}%</div>
            </div>
            <div className={`rounded-lg p-2 border ${PATTERN_STATUS_COLOR[pattern.status] || ""}`}>
              <div className="text-[10px] uppercase">Stato</div>
              <div className="text-base font-bold">{pattern.status}</div>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Error Signature Seed</div>
            <code className="text-xs bg-slate-50 p-2 rounded block break-all">{pattern.error_signature_seed || "—"}</code>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Error Message Regex</div>
            <code className="text-xs bg-slate-50 p-2 rounded block break-all">{pattern.error_message_regex || "—"}</code>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Diff Unified</div>
            <pre className="text-[11px] bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto max-h-80">{pattern.proposed_diff_unified || "—"}</pre>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2">Ultimi outcomes</div>
            {outcomes.length === 0 ? (
              <div className="text-xs text-slate-400 italic">Nessun outcome ancora</div>
            ) : (
              <div className="space-y-1">
                {outcomes.map((o, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={o.status === "applied" ? "text-emerald-700" : "text-rose-700"}>
                      {o.status === "applied" ? "✓" : "✗"}
                    </span>
                    <span className="font-mono text-slate-500">{o.timestamp?.slice(0, 19)}</span>
                    <span>{o.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {actionType && (
            <div className="border-t border-slate-200 pt-3">
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Reason per {actionType === "retire" ? "ritirare" : "riattivare"} (min 10 char)
              </label>
              <textarea data-testid="aisd-pattern-action-reason"
                          value={reason} onChange={(e) => setReason(e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={3} />
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-100">
            Chiudi
          </button>
          <div className="flex gap-2">
            {pattern.status === "active" && !actionType && (
              <button data-testid="aisd-pattern-retire-btn"
                      onClick={() => setActionType("retire")}
                      className="px-3 py-2 text-sm border border-rose-200 text-rose-700 rounded-lg hover:bg-rose-50">
                Ritira
              </button>
            )}
            {pattern.status === "retired" && !actionType && (
              <button data-testid="aisd-pattern-reactivate-btn"
                      onClick={() => setActionType("reactivate")}
                      className="px-3 py-2 text-sm border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50">
                Riattiva
              </button>
            )}
            {actionType && (
              <button data-testid="aisd-pattern-confirm-action"
                      onClick={execute} disabled={busy || reason.length < 10}
                      className="px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 inline animate-spin" /> : "Conferma"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PatternLibraryTab() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (filterStatus) params.set("status", filterStatus);
      if (filterCategory) params.set("fix_category", filterCategory);
      const [sRes, lRes] = await Promise.all([
        axios.get(`${API}/patterns/stats`, { withCredentials: true }),
        axios.get(`${API}/patterns?${params.toString()}`, { withCredentials: true }),
      ]);
      setStats(sRes.data);
      setItems(lRes.data?.items || []);
      setTotal(lRes.data?.total || 0);
    } catch (e) {
      toast.error("Errore caricamento Pattern Library");
    } finally { setLoading(false); }
  }, [filterStatus, filterCategory]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const retire = async (id, reason) => {
    try {
      await axios.post(`${API}/patterns/${id}/retire`, { reason }, { withCredentials: true });
      toast.success("Pattern ritirato"); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const reactivate = async (id, reason) => {
    try {
      await axios.post(`${API}/patterns/${id}/reactivate`, { reason }, { withCredentials: true });
      toast.success("Pattern riattivato"); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="aisd-pattern-library-tab" className="space-y-4">
      {stats && (
        <div data-testid="aisd-pattern-kpis"
              className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <div className="text-[10px] text-emerald-700 uppercase font-semibold">Pattern attivi</div>
            <div className="text-2xl font-bold text-emerald-900">{stats.active}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 uppercase font-semibold">Totali (incl. retired)</div>
            <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
            <div className="text-[10px] text-sky-700 uppercase font-semibold">Applicazioni totali</div>
            <div className="text-2xl font-bold text-sky-900">{stats.total_applications}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="aisd-pattern-savings">
            <div className="text-[10px] text-amber-700 uppercase font-semibold">€ Risparmiati LLM</div>
            <div className="text-2xl font-bold text-amber-900">€{(stats.estimated_eur_saved || 0).toFixed(4)}</div>
            <div className="text-[10px] text-amber-600">{stats.total_llm_calls_saved} chiamate evitate</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 items-center flex-wrap">
        <select data-testid="aisd-pattern-filter-status"
                value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5">
          <option value="">Tutti gli stati</option>
          <option value="active">Active</option>
          <option value="retired">Retired</option>
          <option value="disabled">Disabled</option>
        </select>
        <select data-testid="aisd-pattern-filter-category"
                value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5">
          <option value="">Tutte le categorie</option>
          {["retry", "regenerate_pdf", "clear_cache", "null_check",
             "input_validation", "code_change", "schema_change",
             "manual_review"].map(c => (
              <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button onClick={loadAll} className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50">
          <RefreshCw className="w-3 h-3 inline" /> Aggiorna
        </button>
        <span className="text-xs text-slate-500 ml-auto">{total} patterns</span>
      </div>

      <div data-testid="aisd-pattern-table"
            className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Categoria</th>
              <th className="px-3 py-2 text-left">Path</th>
              <th className="px-3 py-2 text-right">Apps</th>
              <th className="px-3 py-2 text-right">Success</th>
              <th className="px-3 py-2 text-right">Confidence</th>
              <th className="px-3 py-2 text-left">Stato</th>
              <th className="px-3 py-2 text-left">Ultima</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                <Loader2 className="w-5 h-5 inline animate-spin" />
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400 italic">
                Nessun pattern ancora. I pattern si creano automaticamente quando un fix viene applicato con successo.
              </td></tr>
            )}
            {items.map((p) => (
              <tr key={p.pattern_id}
                   data-testid={`aisd-pattern-row-${p.pattern_id}`}
                   onClick={() => setSelectedPattern(p)}
                   className="border-b border-slate-100 hover:bg-emerald-50/30 cursor-pointer">
                <td className="px-3 py-2 font-mono text-[10px]">{p.pattern_id.slice(0, 8)}</td>
                <td className="px-3 py-2">{p.fix_category}</td>
                <td className="px-3 py-2 font-mono text-[10px] truncate max-w-[200px]">{p.file_path_pattern || "—"}</td>
                <td className="px-3 py-2 text-right">{p.applications_count}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-block w-16 bg-slate-100 rounded-full h-2 mr-1 align-middle">
                    <div className="h-2 bg-emerald-500 rounded-full"
                          style={{ width: `${((p.success_rate || 0) * 100)}%` }} />
                  </div>
                  {((p.success_rate || 0) * 100).toFixed(0)}%
                </td>
                <td className="px-3 py-2 text-right">{((p.confidence_score || 0) * 100).toFixed(0)}%</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${PATTERN_STATUS_COLOR[p.status] || ""}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-[10px]">{p.last_used_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PatternDetailModal pattern={selectedPattern}
                            onClose={() => setSelectedPattern(null)}
                            onRetire={retire} onReactivate={reactivate} />
    </div>
  );
}


export default function AdminAISiteDoctorPage() {
  const [stats, setStats] = useState(null);
  const [budget, setBudget] = useState(null);
  const [mode, setMode] = useState(null);
  const [autoAnalyze, setAutoAnalyze] = useState(null);
  const [applications, setApplications] = useState(null);
  const [selftestSteps, setSelftestSteps] = useState(null);
  const [activeTab, setActiveTab] = useState("errors"); // errors | budget | health | patterns
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({
    search: "", severity: "", source: "", status: "", range: "7d",
  });

  const dateRangeParams = useMemo(() => {
    const now = new Date();
    if (filters.range === "all") return {};
    let from;
    if (filters.range === "24h") from = new Date(now - 24 * 3600 * 1000);
    else if (filters.range === "7d") from = new Date(now - 7 * 86400 * 1000);
    else if (filters.range === "30d") from = new Date(now - 30 * 86400 * 1000);
    return from ? { from_date: from.toISOString() } : {};
  }, [filters.range]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/errors/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Login admin richiesto");
    }
  }, []);

  const loadBudget = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/budget`, { withCredentials: true });
      setBudget(data);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Login admin richiesto");
    }
  }, []);

  const loadMode = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mode`, { withCredentials: true });
      setMode(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadAutoAnalyze = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auto-analyze`, { withCredentials: true });
      setAutoAnalyze(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadApplications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/applications?limit=20`, { withCredentials: true });
      setApplications(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadSelftestSteps = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/selftest/steps`, { withCredentials: true });
      setSelftestSteps(data);
    } catch (e) { /* silent */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize, offset: page * pageSize,
        search: filters.search || undefined,
        severity: filters.severity || undefined,
        source: filters.source || undefined,
        status: filters.status || undefined,
        ...dateRangeParams,
      };
      const { data } = await axios.get(`${API}/errors`, {
        params, withCredentials: true,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Login admin richiesto");
      else toast.error("Errore caricamento errori");
    } finally { setLoading(false); }
  }, [page, pageSize, filters, dateRangeParams]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadBudget(); }, [loadBudget]);
  useEffect(() => { loadMode(); }, [loadMode]);
  useEffect(() => { loadAutoAnalyze(); }, [loadAutoAnalyze]);
  useEffect(() => { loadApplications(); }, [loadApplications]);
  useEffect(() => { loadSelftestSteps(); }, [loadSelftestSteps]);

  const openDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/errors/${id}`, { withCredentials: true });
      setDetail(data);
    } catch (e) {
      toast.error("Errore caricamento dettaglio");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.post(`${API}/errors/${id}/status`, { status },
                        { withCredentials: true });
      toast.success(`Status aggiornato → ${status}`);
      await loadRows();
      await loadStats();
    } catch (e) {
      toast.error("Errore aggiornamento status");
      throw e;
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.severity) params.append("severity", filters.severity);
      if (filters.source) params.append("source", filters.source);
      if (filters.status) params.append("status", filters.status);
      if (dateRangeParams.from_date) params.append("from_date", dateRangeParams.from_date);
      const res = await axios.get(`${API}/errors-export.csv?${params.toString()}`,
                                    { responseType: "blob", withCredentials: true });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `site-doctor-errors-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success("CSV scaricato");
    } catch (e) {
      toast.error("Export CSV fallito");
    }
  };

  const sevCounts = stats?.count_by_severity || {};
  const srcCounts = stats?.count_by_source || {};

  return (
    <div data-testid="admin-ai-site-doctor-page"
         className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-8 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <Zap className="w-6 h-6 text-emerald-700" />
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              AI Site Doctor
            </h1>
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-medium">
              FASE 7B — Dashboard
            </span>
          </div>
          <p className="text-sm text-slate-600">
            Monitoraggio centralizzato degli errori full-stack. Cattura, deduplica e classifica.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 space-y-5">
        {/* Tab switcher + Budget widget */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1" data-testid="aisd-tabs">
            <button data-testid="aisd-tab-errors"
                    onClick={() => setActiveTab("errors")}
                    className={`px-4 py-1.5 text-sm rounded-lg ${activeTab === "errors" ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              Errori
            </button>
            <button data-testid="aisd-tab-budget"
                    onClick={() => setActiveTab("budget")}
                    className={`px-4 py-1.5 text-sm rounded-lg ${activeTab === "budget" ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              Budget AI
            </button>
            <button data-testid="aisd-tab-health"
                    onClick={() => setActiveTab("health")}
                    className={`px-4 py-1.5 text-sm rounded-lg ${activeTab === "health" ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              Salute & Notifiche
            </button>
            <button data-testid="aisd-tab-patterns"
                    onClick={() => setActiveTab("patterns")}
                    className={`px-4 py-1.5 text-sm rounded-lg ${activeTab === "patterns" ? "bg-emerald-700 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              🧬 Pattern Library
            </button>
          </div>
          <BudgetWidget budget={budget} onClick={() => setActiveTab("budget")} />
        </div>

        {activeTab === "budget" ? (
          <BudgetTab budget={budget} mode={mode} autoAnalyze={autoAnalyze}
                      applications={applications}
                      selftestSteps={selftestSteps}
                      onUpdate={loadBudget}
                      onModeUpdate={loadMode}
                      onAutoUpdate={loadAutoAnalyze}
                      onApplicationsRefresh={loadApplications}
                      onSelftestRefresh={loadSelftestSteps} />
        ) : activeTab === "health" ? (
          <HealthNotificationsTab />
        ) : activeTab === "patterns" ? (
          <PatternLibraryTab />
        ) : (
        <>
        {/* Health banner */}
        <HealthBanner stats={stats} />

        {/* KPI cards */}
        <div data-testid="aisd-kpi-grid"
             className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KPICard
            testid="aisd-kpi-today"
            label="Errori oggi"
            value={stats?.count_today ?? "—"}
            sublabel={`${stats?.count_total ?? 0} totali (90gg)`}
            icon={Activity}
            color="emerald"
          />
          <KPICard
            testid="aisd-kpi-critical"
            label="Critici aperti"
            value={sevCounts.critical || 0}
            sublabel={`${sevCounts.high || 0} di alta gravità`}
            icon={AlertTriangle}
            color="red"
          />
          <KPICard
            testid="aisd-kpi-frontend"
            label="Frontend / Backend"
            value={`${srcCounts.frontend || 0} / ${srcCounts.backend || 0}`}
            sublabel={`API ${srcCounts.api || 0} · DB ${srcCounts.database || 0}`}
            icon={Filter}
            color="indigo"
          />
          <div data-testid="aisd-top-recurring"
                className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Top ricorrenti</span>
              <RefreshCw className="w-4 h-4 text-amber-600" />
            </div>
            {(stats?.top_5_recurring || []).slice(0, 3).map((r, i) => (
              <div key={i} className="text-xs text-slate-700 truncate py-0.5">
                <span className="font-bold text-amber-700">×{r.occurrences_count}</span>{" "}
                <span title={r.error_message}>{(r.error_message || "—").slice(0, 38)}</span>
              </div>
            ))}
            {(!stats?.top_5_recurring || stats.top_5_recurring.length === 0) && (
              <div className="text-xs text-slate-400 italic">Nessuno</div>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <FilterBar filters={filters} setFilters={(f) => { setFilters(f); setPage(0); }}
                    onExport={exportCsv}
                    onRefresh={() => { loadRows(); loadStats(); }}
                    loading={loading} />

        {/* Table */}
        <div data-testid="aisd-errors-table"
              className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Messaggio</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Occ.</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">AI</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-700" />
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">
                    <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                    Nessun errore trovato per i filtri selezionati.
                  </td></tr>
                )}
                {!loading && rows.map((row) => (
                  <ErrorRow key={row.id} row={row} onClick={openDetail} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <span className="text-xs text-slate-600">
                {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} di {total}
              </span>
              <div className="flex gap-2">
                <button data-testid="aisd-page-prev"
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-30">
                  ←
                </button>
                <button data-testid="aisd-page-next"
                        disabled={(page + 1) * pageSize >= total}
                        onClick={() => setPage(page + 1)}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-100 disabled:opacity-30">
                  →
                </button>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>

      <DetailModal detail={detail}
                    onClose={() => setDetail(null)}
                    onStatusChange={changeStatus}
                    onAnalyzed={() => { loadRows(); loadBudget(); }}
                    mode={mode} />
    </div>
  );
}
